import { isFeatureEnabled } from "@/env";
import { isWeapon } from "@/services/Helpers";
import { getGoldCostSelector, QuestHeroSelections, questStartingGold } from "@/services/QuestService";
import { nanoid } from "nanoid";
import { FEATURE_FLAGS } from "./globals";
import { IQuestConsumable, IQuestSkill, IUpgradeOption, IUpgradeSection } from "./types";

const ENABLE_V36 = isFeatureEnabled(FEATURE_FLAGS.V3_6);

export const defaultQuestSkill: IQuestSkill = {
  id: undefined,
  name: "",
  sqName: "",
  rule: "",
  tier: 0,
  type: 0,
  classId: "",
  costFormula: []
};

export const defaultQuestConsumable: IQuestConsumable = {
  id: undefined,
  name: "",
  sqName: "",
  baseName: "",
  tier: 0,
  description: "",
  costFormula: [{ type: "cost", value: 0 }]
};

export const defaultQuestHero = (sq: boolean) =>
  ({
    id: nanoid(),
    name: "",
    sq,
    rules: [],
    equipment: [],
    quality: 6,
    defense: 6,
    tough: 9,
    end: 3,
    str: 6,
    dex: 6,
    wil: 6,
    cost: 10,
    feats: [],

    selections: {
      class: { id: null, name: "" },
      faction: { id: null, name: "" },
      baseHero: { id: null, name: "" }
    },

    actions: [],

    tracking: {
      gold: 0,
      stress: 0,
      wounds: 0,
      conditions: []
    }
  }) as QuestHeroSelections;

// Must be in level order desc
export const questTiers = [
  { tier: 3, level: 9 },
  { tier: 2, level: 5 },
  { tier: 1, level: 2 }
];
// TODO: automate this - grabbing the "starter" skill from the stats that have been upgraded

// DL_QqAgFY_Kq = Mystic Warding

export const selectDefaultGear = (
  baseHeroId: string,
  sections: IUpgradeSection[],
  baseTough: number,
  ranger: boolean
) => {
  if (ENABLE_V36) {
    return selectDefaultGear_v36(sections);
  } else {
    return selectDefaultGear_v35(baseHeroId, sections, baseTough, ranger);
  }
};

export const selectDefaultGear_v35 = (
  baseHeroId: string,
  sections: IUpgradeSection[],
  baseTough: number,
  ranger: boolean
) => {
  const results: IUpgradeOption[] = [];
  let spend = 0;

  const costSelector = getGoldCostSelector({ selections: { baseHero: { id: baseHeroId } } }, { baseTough });
  const getOptionCost = (option: IUpgradeOption) => option.costs.find(costSelector)?.cost;
  const validChoice = (cost: number) => spend + cost <= questStartingGold;
  const priority = ["Mystic Warding", "Light Armor"];

  const getOptionByName = (name: string) => {
    for (const section of sections) {
      for (const option of section.options) {
        const optionCost = getOptionCost(option);
        if (!validChoice(optionCost)) {
          continue;
        }

        for (const gain of option.gains) {
          if (gain.name === name) {
            spend += optionCost;
            return option;
          }
        }
      }
    }
  };

  // Rangers would like to buy a bow
  if (ranger) {
    let maxCost = 0;
    let matchOption = null;
    for (const section of sections) {
      for (const option of section.options) {
        const optionCost = getOptionCost(option);
        for (const gain of option.gains) {
          if (!isWeapon(gain) || !(gain.range > 0)) continue;
          if (optionCost > maxCost && optionCost <= 25) {
            maxCost = optionCost;
            matchOption = option;
          }
        }
      }
    }

    if (matchOption) {
      spend += maxCost;
      results.push(matchOption);
    }
  }

  // Try and apply a piece of defensive gear
  for (const search of priority) {
    const option = getOptionByName(search);
    if (option) {
      results.push(option);
    }
  }

  const addRecursive = (name, sqName) => {
    let loopsRemaining = 10;
    while (loopsRemaining-- > 0 && spend < questStartingGold) {
      const option = getOptionByName(name) || getOptionByName(sqName);
      console.log(option ? `Found ${option.label}. Spend: ${spend}` : `${name}/${sqName} not found`);
      if (option) {
        results.push(option);
      } else {
        break;
      }
    }
  };

  // Then apply a bunch of health pots
  addRecursive("Health Potion", "Med Kit");

  // Then apply a bunch of curing pots
  addRecursive("Curing Potion", "Curing Kit");

  return results;
};

export const selectDefaultGear_v36 = (sections: IUpgradeSection[]) => {
  const results: IUpgradeOption[] = [];

  const priority = ["Mystic Warding", "Light Armor"];

  const getOptionByName = (name: string) => {
    for (const section of sections) {
      for (const option of section.options) {
        for (const gain of option.gains) {
          if (gain.name === name) {
            return option;
          }
        }
      }
    }
  };

  // Try and apply a piece of defensive gear
  for (const search of priority) {
    const option = getOptionByName(search);
    if (option) {
      results.push(option);
    }
  }

  const addOption = (name, sqName) => {
    const option = getOptionByName(name) || getOptionByName(sqName);
    console.log(option ? `Found ${option.label}.` : `${name}/${sqName} not found`);
    if (option) {
      results.push(option);
    }
  };

  // Then apply a bunch of health pots
  addOption("Health Potion", "Med Kit");

  // Then apply a bunch of curing pots
  addOption("Curing Potion", "Curing Kit");

  return results;
};

export const classDefaults = [
  {
    classId: "M8aYY6WE1qVa", //"Barbarian",
    abilityChoices: ["str", "str"],
    combatStatChoices: ["OneQua", "OneSkillOneEnd"],
    skillChoices: [
      "bGQNESrUXlHQtCcT9_D1v" // Rage
    ]
  },
  {
    classId: "l5v__VUG-3xB", //"Bard",
    abilityChoices: ["dex", "wil"],
    combatStatChoices: ["TwoSkills", "TwoEnd"],
    skillChoices: [
      "SjOs8k1k1lqTWxnUqaAbH", // Inspire
      "QP3VAzUromPtBxvIwX4XH" // Song of Health
    ]
  },
  {
    classId: "xz40mWAP4xbJ", //"Cleric",
    abilityChoices: ["wil", "wil"],
    combatStatChoices: ["OneSkillOneEnd", "OneQua"],
    skillChoices: [
      "MwW0jzPZJh3K_TewZX38y" // Healing Word
    ]
  },
  {
    classId: "3ArZ3c68AHn_", //"Druid",
    abilityChoices: ["dex", "wil"],
    combatStatChoices: ["OneCaster", "TwoSkills"],
    skillChoices: [
      "BXVylVl1ldDZtKQtlkTJf", // Barkskin
      "ttKkihk8ilW8k9i_OEQvR" // Fog Cloud
    ]
  },
  {
    classId: "9xcr_eEryryh", //"Fighter",
    abilityChoices: ["str", "str"],
    combatStatChoices: ["OneQua", "OneSkillOneEnd"],
    skillChoices: [
      "0R25kxAcCpCViyoMQHD8c" // Combat Superiority
    ]
  },
  {
    classId: "_CMIQRUiEcIB", //"Monk",
    abilityChoices: ["str", "dex"],
    combatStatChoices: ["TwoSkills", "OneQua"],
    skillChoices: [
      "LMGSjIv3Mz6DqQX1Ud49i", // Flurry of Blows
      "o5WaI9CNDv2UgA5GkChI0" // Lightning Reflexes
    ]
  },
  {
    classId: "kBanbvQ3ri3t", //"Paladin",
    abilityChoices: ["wil", "wil"],
    combatStatChoices: ["OneQua", "OneSkillOneEnd"],
    skillChoices: [
      "dUQ93R1J5S-MXirCjDu2P" // Ancient Weapon
    ]
  },
  {
    classId: "R0WM1rvOqqVV", //"Ranger",
    abilityChoices: ["str", "dex"],
    combatStatChoices: ["OneQua", "TwoSkills"],
    skillChoices: [
      "yiQBhE2aXps_ykWYscJtZ", // Hunting Beast
      "4yazucezkW-w_qT2IHbGG" // Dread Attack
    ]
  },
  {
    classId: "o7iuWPVnPdc0", //"Rogue",
    abilityChoices: ["dex", "dex"],
    combatStatChoices: ["OneSkillOneEnd", "OneQua"],
    skillChoices: [
      "jtW9htNgg1-EpYmEniT00" // Sleight of Hand
    ]
  },
  {
    classId: "b-yiaT6j0xub", //"Sorcerer",
    abilityChoices: ["str", "wil"],
    combatStatChoices: ["OneCaster", "TwoSkills"],
    skillChoices: [
      "GPZ7egoNmbPUce97f3efB", // Tides of Chaos
      "wr7iO4BLBAuc_Lp4hMfvD" // Distant Spell
    ]
  },
  {
    classId: "YK9Av5Fja4_W", //"Warlock",
    abilityChoices: ["wil", "wil"],
    combatStatChoices: ["OneCaster", "OneSkillOneEnd"],
    skillChoices: [
      "m3hSj4gWXPve6pyflCa9f" // Dark Warding
    ]
  },
  {
    classId: "AUrVEXo7SvLE", //"Wizard",
    abilityChoices: ["dex", "wil"],
    combatStatChoices: ["OneCaster", "TwoSkills"],
    skillChoices: [
      "bibq5AqgAR8BRZVVJdXhf", // Enchantment
      "J5Hlk4SYAIA8k_a8pdORA" // Arcana
    ]
  }
];
